/**
 * Welded polygon meshes for the modifier idioms. A build mesh is triangles
 * with split vertices; subdivision, bevel and solidify need shared vertices
 * and the quads a modeller drew. `readPoly` welds by position and pairs
 * coplanar triangles back into quads (or, for bevel, whole n-gons);
 * `writePoly` triangulates the result
 * into the same Mesh, keeping its material slots.
 *
 * Faces are counter-clockwise seen from outside (glTF). A smooth face shares
 * angle-weighted normals with the smooth faces around each vertex; a flat
 * face keeps its own, which is Blender's use_smooth.
 */
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';
import { SubMesh } from '@babylonjs/core/Meshes/subMesh';
import { VertexBuffer } from '@babylonjs/core/Buffers/buffer';
import { Material } from '@babylonjs/core/Materials/material';
import { triangulate } from './mesh-earcut';

export type V3 = number[];
export interface Face {
    v: number[];
    /** Material index into the mesh's MultiMaterial, 0 for a single material. */
    slot: number;
    smooth: boolean;
    /** Per-corner UVs; faces without them get a metre-scale box projection. */
    uv?: number[][];
}
export interface Poly { verts: V3[]; faces: Face[] }

export const V = {
    add: (a: V3, b: V3): V3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]],
    sub: (a: V3, b: V3): V3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]],
    scale: (a: V3, s: number): V3 => [a[0] * s, a[1] * s, a[2] * s],
    dot: (a: V3, b: V3): number => a[0] * b[0] + a[1] * b[1] + a[2] * b[2],
    cross: (a: V3, b: V3): V3 => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]],
    len: (a: V3): number => Math.hypot(a[0], a[1], a[2]),
    norm: (a: V3): V3 => {
        const l = Math.hypot(a[0], a[1], a[2]);
        return l > 1e-12 ? [a[0] / l, a[1] / l, a[2] / l] : [0, 0, 0];
    },
    lerp: (a: V3, b: V3, t: number): V3 => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t],
    dist: (a: V3, b: V3): number => Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]),
};

export const edgeKey = (a: number, b: number): string => (a < b ? `${a},${b}` : `${b},${a}`);

/** Newell normal, unit; zero for a degenerate face. */
export function faceNormal(verts: V3[], ids: number[]): V3 {
    let x = 0, y = 0, z = 0;
    for (let i = 0; i < ids.length; i++) {
        const a = verts[ids[i]], b = verts[ids[(i + 1) % ids.length]];
        x += (a[1] - b[1]) * (a[2] + b[2]);
        y += (a[2] - b[2]) * (a[0] + b[0]);
        z += (a[0] - b[0]) * (a[1] + b[1]);
    }
    return V.norm([x, y, z]);
}

export function faceCenter(verts: V3[], ids: number[]): V3 {
    let c: V3 = [0, 0, 0];
    for (const i of ids) c = V.add(c, verts[i]);
    return V.scale(c, 1 / ids.length);
}

export function bounds(verts: V3[]): { min: V3; max: V3 } {
    const min = [Infinity, Infinity, Infinity], max = [-Infinity, -Infinity, -Infinity];
    for (const p of verts) for (let k = 0; k < 3; k++) { min[k] = Math.min(min[k], p[k]); max[k] = Math.max(max[k], p[k]); }
    return { min, max };
}

/** Points within `tolerance` of an earlier one map onto it. */
function weldPoints(p: ArrayLike<number>, tolerance: number): { verts: V3[]; remap: number[] } {
    const cell = Math.max(tolerance * 2, 1e-9);
    const grid = new Map<string, number[]>();
    const verts: V3[] = [];
    const remap: number[] = [];
    for (let i = 0; i < p.length; i += 3) {
        const q = [p[i], p[i + 1], p[i + 2]];
        const k = q.map(x => Math.round(x / cell));
        let found = -1;
        for (let dx = -1; dx <= 1 && found < 0; dx++) for (let dy = -1; dy <= 1 && found < 0; dy++) for (let dz = -1; dz <= 1 && found < 0; dz++) {
            for (const j of grid.get(`${k[0] + dx},${k[1] + dy},${k[2] + dz}`) ?? []) {
                if (V.dist(verts[j], q) <= tolerance) { found = j; break; }
            }
        }
        if (found < 0) {
            found = verts.push(q) - 1;
            const key = k.join(',');
            const list = grid.get(key);
            if (list) list.push(found); else grid.set(key, [found]);
        }
        remap.push(found);
    }
    return { verts, remap };
}

/** Babylon's index order on this mesh is clockwise seen from outside. */
const clockwise = (mesh: Mesh): boolean => mesh.sideOrientation !== Material.CounterClockWiseSideOrientation;

/**
 * The mesh's own vertices (bake its transform first), welded, as the
 * polygons its triangles came from. `ngons` also rejoins fanned n-gons,
 * which bevel wants and subdivision doesn't (see joinFaces).
 */
export function readPoly(mesh: Mesh, tolerance = 1e-6, ngons = false): Poly {
    const p = mesh.getVerticesData(VertexBuffer.PositionKind) ?? [];
    const normals = mesh.getVerticesData(VertexBuffer.NormalKind);
    const uvs = mesh.getVerticesData(VertexBuffer.UVKind);
    const indices = mesh.getIndices() ?? Array.from({ length: p.length / 3 }, (_, i) => i);
    const { verts, remap } = weldPoints(p, tolerance);
    const flip = clockwise(mesh);
    const ranges = mesh.subMeshes?.length
        ? mesh.subMeshes.map(sm => ({ slot: sm.materialIndex, start: sm.indexStart, count: sm.indexCount }))
        : [{ slot: 0, start: 0, count: indices.length }];
    const tris: Face[] = [];
    for (const r of ranges) {
        for (let i = r.start; i < r.start + r.count; i += 3) {
            const c = flip ? [indices[i], indices[i + 2], indices[i + 1]] : [indices[i], indices[i + 1], indices[i + 2]];
            const v = c.map(k => remap[k]);
            if (new Set(v).size < 3) continue;
            const n = faceNormal(verts, v);
            if (!n[0] && !n[1] && !n[2]) continue;
            // Smooth when any authored corner normal leans off the face's own.
            // A flat face's normals match it to float precision; a fine smooth
            // sphere's lean only a degree or so.
            const smooth = !!normals && c.some(k => V.dot(n, [normals[k * 3], normals[k * 3 + 1], normals[k * 3 + 2]]) < 1 - 1e-5);
            tris.push({ v, slot: r.slot, smooth, uv: uvs ? c.map(k => [uvs[k * 2], uvs[k * 2 + 1]]) : undefined });
        }
    }
    return { verts, faces: joinFaces(verts, tris, ngons) };
}

const sameUv = (x: number[] | undefined, y: number[] | undefined): boolean =>
    !x || !y || (Math.abs(x[0] - y[0]) < 1e-6 && Math.abs(x[1] - y[1]) < 1e-6);

/**
 * Rejoin coplanar triangles in quad pairs. With `ngons`, a region whose
 * vertices all lie on its one boundary loop becomes that n-gon instead:
 * left as a fan (fromPydata's caps), its near-parallel inner edges pinch a
 * bevel along the rim to a sliver. Subdivision skips this, because a strip
 * of drawn quads fits the same test and would lose its inner edges.
 */
function joinFaces(verts: V3[], tris: Face[], ngons: boolean): Face[] {
    const normals = tris.map(t => faceNormal(verts, t.v));
    const owner = new Map<string, { t: number; k: number }>();
    tris.forEach((t, i) => t.v.forEach((a, k) => owner.set(`${a},${t.v[(k + 1) % 3]}`, { t: i, k })));
    const parent = tris.map((_, i) => i);
    const find = (i: number): number => { while (parent[i] !== i) i = parent[i] = parent[parent[i]]; return i; };
    tris.forEach((t, i) => t.v.forEach((a, k) => {
        const b = t.v[(k + 1) % 3], o = owner.get(`${b},${a}`);
        if (!o) return;
        const u = tris[o.t];
        if (u.slot !== t.slot || u.smooth !== t.smooth || V.dot(normals[i], normals[o.t]) < 1 - 1e-9) return;
        if (!sameUv(t.uv?.[k], u.uv?.[(o.k + 1) % 3]) || !sameUv(t.uv?.[(k + 1) % 3], u.uv?.[o.k])) return;
        parent[find(i)] = find(o.t);
    }));
    const groups = new Map<number, number[]>();
    tris.forEach((_, i) => { const r = find(i); const g = groups.get(r); if (g) g.push(i); else groups.set(r, [i]); });
    const out: Face[] = [];
    for (const members of groups.values()) {
        const polygon = ngons && members.length > 2 ? ngon(tris, members, find) : null;
        if (polygon) out.push(polygon);
        else out.push(...pairQuads(verts, members.map(i => tris[i])));
    }
    return out;
}

/** The region's single boundary loop as one face, or null when it has inner vertices or several loops. */
function ngon(tris: Face[], members: number[], find: (i: number) => number): Face | null {
    const root = find(members[0]);
    const inside = new Set<string>();
    for (const i of members) tris[i].v.forEach((a, k) => inside.add(`${a},${tris[i].v[(k + 1) % 3]}`));
    const next = new Map<number, { b: number; uv?: number[] }>();
    const all = new Set<number>();
    for (const i of members) {
        const t = tris[i];
        for (let k = 0; k < 3; k++) {
            const a = t.v[k], b = t.v[(k + 1) % 3];
            all.add(a);
            if (inside.has(`${b},${a}`)) continue;
            if (next.has(a)) return null;
            next.set(a, { b, uv: t.uv?.[k] });
        }
    }
    if (next.size !== all.size) return null;
    const start = next.keys().next().value as number;
    const loop: number[] = [], uv: number[][] = [];
    for (let a = start; loop.length <= next.size; ) {
        const e = next.get(a)!;
        loop.push(a);
        if (e.uv) uv.push(e.uv);
        a = e.b;
        if (a === start) break;
    }
    if (loop.length !== next.size) return null;
    const t = tris[root];
    return { v: loop, slot: t.slot, smooth: t.smooth, uv: uv.length === loop.length ? uv : undefined };
}

/**
 * Join each triangle to the coplanar neighbour across its longest edge when
 * that edge is the neighbour's longest too: the diagonal of the quad both
 * came from. Slot, shading and UVs must agree across it.
 */
function pairQuads(verts: V3[], tris: Face[]): Face[] {
    const longest = tris.map(t => {
        let best = 0, bestLen = -1;
        for (let k = 0; k < 3; k++) {
            const l = V.dist(verts[t.v[k]], verts[t.v[(k + 1) % 3]]);
            if (l > bestLen + 1e-12) { best = k; bestLen = l; }
        }
        return best;
    });
    const owner = new Map<string, number>();
    tris.forEach((t, i) => owner.set(`${t.v[longest[i]]},${t.v[(longest[i] + 1) % 3]}`, i));
    const normals = tris.map(t => faceNormal(verts, t.v));
    const used = new Set<number>();
    const out: Face[] = [];
    tris.forEach((t, i) => {
        if (used.has(i)) return;
        const k = longest[i], a = t.v[k], b = t.v[(k + 1) % 3], c = t.v[(k + 2) % 3];
        const j = owner.get(`${b},${a}`);
        const u = j === undefined ? undefined : tris[j];
        if (j === undefined || !u || used.has(j) || j === i || u.slot !== t.slot || u.smooth !== t.smooth
            || V.dot(normals[i], normals[j]) < 1 - 1e-9) {
            out.push(t);
            return;
        }
        const ju = u.v.indexOf(b), d = u.v[(ju + 2) % 3];
        if (!sameUv(t.uv?.[k], u.uv?.[(ju + 1) % 3]) || !sameUv(t.uv?.[(k + 1) % 3], u.uv?.[ju])) {
            out.push(t);
            return;
        }
        used.add(i); used.add(j);
        out.push({
            v: [a, d, b, c], slot: t.slot, smooth: t.smooth,
            uv: t.uv && u.uv ? [t.uv[k], u.uv[(ju + 2) % 3], t.uv[(k + 1) % 3], t.uv[(k + 2) % 3]] : undefined,
        });
    });
    return out;
}

/** Triangulate one planar polygon in its own plane; triples index into `ids`, counter-clockwise. */
export function triangulateFace(verts: V3[], ids: number[]): number[][] {
    if (ids.length === 3) return [[0, 1, 2]];
    const normal = faceNormal(verts, ids);
    const u = V.norm(V.cross(Math.abs(normal[0]) > 0.9 ? [0, 1, 0] : [1, 0, 0], normal)), w = V.cross(normal, u);
    const flat = ids.flatMap(i => [V.dot(verts[i], u), V.dot(verts[i], w)]);
    const out = triangulate(flat);
    const tris: number[][] = [];
    for (let i = 0; i < out.length; i += 3) tris.push([out[i], out[i + 1], out[i + 2]]);
    return tris;
}

/** Box projection by dominant normal axis, 1 m = 1 UV. */
function boxUv(p: V3, n: V3): number[] {
    const a = n.map(Math.abs);
    if (a[2] >= a[0] && a[2] >= a[1]) return [p[0], p[1]];
    return a[0] >= a[1] ? [p[2], p[1]] : [p[0], p[2]];
}

function cornerAngle(verts: V3[], ids: number[], c: number): number {
    const n = ids.length, p = verts[ids[c]];
    const d1 = V.norm(V.sub(verts[ids[(c + n - 1) % n]], p)), d2 = V.norm(V.sub(verts[ids[(c + 1) % n]], p));
    return Math.acos(Math.max(-1, Math.min(1, V.dot(d1, d2))));
}

const KEPT_KINDS = new Set<string>([VertexBuffer.PositionKind, VertexBuffer.NormalKind, VertexBuffer.UVKind]);

/**
 * Replace the mesh's geometry with `poly`: one submesh per slot, positions,
 * normals and UVs. Returns the other vertex attributes it dropped (colours,
 * UV2, skin weights), which no longer match the new vertices.
 */
export function writePoly(poly: Poly, mesh: Mesh): string[] {
    const { verts, faces } = poly;
    const fn = faces.map(f => faceNormal(verts, f.v));
    const shared: V3[] = verts.map(() => [0, 0, 0]);
    faces.forEach((f, i) => {
        if (f.smooth) f.v.forEach((v, c) => { shared[v] = V.add(shared[v], V.scale(fn[i], cornerAngle(verts, f.v, c))); });
    });
    const positions: number[] = [], normals: number[] = [], uvs: number[] = [];
    const cache = new Map<string, number>();
    const bySlot = new Map<number, number[]>();
    const flip = clockwise(mesh);
    faces.forEach((f, i) => {
        if (!fn[i][0] && !fn[i][1] && !fn[i][2]) return;
        const corner = f.v.map((v, c) => {
            const n = f.smooth && V.len(shared[v]) > 1e-12 ? V.norm(shared[v]) : fn[i];
            const uv = f.uv?.[c] ?? boxUv(verts[v], fn[i]);
            const key = `${v}|${n.map(x => x.toFixed(5)).join(',')}|${uv[0].toFixed(6)},${uv[1].toFixed(6)}`;
            let at = cache.get(key);
            if (at === undefined) {
                at = positions.length / 3;
                cache.set(key, at);
                positions.push(...verts[v]);
                normals.push(...n);
                uvs.push(uv[0], uv[1]);
            }
            return at;
        });
        let list = bySlot.get(f.slot);
        if (!list) bySlot.set(f.slot, (list = []));
        for (const [a, b, c] of triangulateFace(verts, f.v)) {
            if (flip) list.push(corner[a], corner[c], corner[b]);
            else list.push(corner[a], corner[b], corner[c]);
        }
    });
    if (mesh.geometry && mesh.geometry.meshes.length > 1) mesh.makeGeometryUnique();
    const dropped = mesh.getVerticesDataKinds().filter(kind => !KEPT_KINDS.has(kind));
    for (const kind of dropped) mesh.removeVerticesData(kind);
    const indices: number[] = [];
    const ranges: { slot: number; start: number; count: number }[] = [];
    for (const slot of [...bySlot.keys()].sort((a, b) => a - b)) {
        const list = bySlot.get(slot)!;
        ranges.push({ slot, start: indices.length, count: list.length });
        for (const k of list) indices.push(k);
    }
    const data = new VertexData();
    data.positions = positions;
    data.normals = normals;
    data.uvs = uvs;
    data.indices = indices;
    data.applyToMesh(mesh);
    mesh.releaseSubMeshes();
    for (const r of ranges) SubMesh.AddToMesh(r.slot, 0, positions.length / 3, r.start, r.count, mesh);
    mesh.refreshBoundingInfo();
    return dropped;
}
